import * as React from 'react';
import { ChevronDown, Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  ErrorBanner,
} from '@klareospace/pharos-lib-web-utils';
import type { ApiErrorDetail, ImportSupportedExtension } from '@klareospace/pharos-lib-types';

import type { ImportTypeConfig } from '../config';
import { useDownloadTemplate } from '../hooks';
import { resolveImportErrorMessage } from '../utils';
import { FileDropzone } from './file-dropzone';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ImportDataCardProps {
  config: ImportTypeConfig;
  error: ApiErrorDetail | null;
  isUploading: boolean;
  onFileSelected: (file: File) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Upload card shown on the import screen — template info, the dropzone, and error reporting. */
export function ImportDataCard({ config, error, isUploading, onFileSelected }: ImportDataCardProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('imports');
  const [hasValidationError, setHasValidationError] = React.useState(false);
  const downloadTemplate = useDownloadTemplate(config);

  const isDownloadDisabled = !config.templateEndpoint || downloadTemplate.isPending;

  function handleDownloadClick(format: ImportSupportedExtension): void {
    downloadTemplate.mutate(format);
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileSpreadsheet className="h-5 w-5 text-primary" />
          {t('importData.title')}
        </CardTitle>
        <CardDescription>{t('importData.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between gap-4 rounded-lg border bg-muted/30 p-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <FileSpreadsheet className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">{t(config.templateNameKey)}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{t('importData.template.format')}</p>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label={t('importData.template.menuAriaLabel')}
                disabled={isDownloadDisabled}
                size="sm"
                type="button"
                variant="outline"
              >
                {downloadTemplate.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                {tCommon('actions.download')}
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(): void => handleDownloadClick('csv')}>
                <FileText className="h-4 w-4" />
                <div className="flex flex-col">
                  <span className="font-medium">{t('importData.template.csv')}</span>
                  <span className="text-xs text-muted-foreground">{t('importData.template.csvDescription')}</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(): void => handleDownloadClick('xlsx')}>
                <FileSpreadsheet className="h-4 w-4" />
                <div className="flex flex-col">
                  <span className="font-medium">{t('importData.template.xlsx')}</span>
                  <span className="text-xs text-muted-foreground">{t('importData.template.xlsxDescription')}</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <FileDropzone
          isUploading={isUploading}
          onFileSelected={onFileSelected}
          onValidationErrorChange={setHasValidationError}
        />

        {/* The dropzone's local validation alert and this mutation error banner must never both
            show — the local error takes priority since it reflects the user's most recent action. */}
        {!hasValidationError && error && <ErrorBanner message={resolveImportErrorMessage(t, error)} />}
      </CardContent>
    </Card>
  );
}
